import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import { ArrowUp, ArrowUpRight, Check } from 'lucide-react'
import { LogoMark } from '@/components/generals/logo'
import { PageLink } from '@/components/kit/page-link'

import {
    DGII_NOTICE,
    footerCompany,
    footerProduct,
    footerResources,
    LOGIN_HREF,
    siteConfig,
    START_HREF,
    START_LABEL,
    TAGLINE
} from '@/constants/site'

const columnTitle = 'font-mono text-[11px] uppercase tracking-[0.16em] text-stone'

const trialPoints = ['Plan Pro completo', 'Sin tarjeta de crédito', 'Soporte en República Dominicana']

const FooterColumn: FC<{ title: string, links: Array<NavItem> }> = ({ title, links }) => {
    return (
        <div>
            <p className={columnTitle}>{title}</p>

            <ul className='mt-5 flex flex-col gap-2.5'>
                {links.map(link => (
                    <li key={link.href}>
                        <PageLink
                            href={link.href}
                            className='group inline-flex items-center gap-2 text-[15px] text-ink/80 transition-colors hover:text-ink'
                        >
                            <span className='h-px w-0 bg-ember transition-[width] duration-500 ease-expo group-hover:w-3' />
                            {link.label}
                        </PageLink>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const Footer: FC = (): ReactNode => {
    const toTop = () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
    }

    return (
        <footer className='relative overflow-hidden pt-24 md:pt-32'>
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='rule-double flex flex-wrap items-center justify-between gap-3 pt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>
                    <span>Himelio · cierre de página</span>
                    <span className='hidden md:inline'>{TAGLINE}</span>
                    <span>Hecho para negocios de República Dominicana</span>
                </div>

                <div className='mt-12 grid gap-16 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='relative rounded-[1.75rem] border border-ink/10 bg-paper p-7 shadow-[0_24px_50px_-36px_rgb(0_0_0/0.45)] md:p-9'>
                            <span
                                aria-hidden='true'
                                className='tape -top-3 left-10 -rotate-6'
                            />

                            <div className='flex items-start justify-between gap-4'>
                                <p className={columnTitle}>Prueba gratis</p>
                                <span className='stamp -rotate-6 text-ember'>14 días</span>
                            </div>

                            <h2 className='mt-5 text-[clamp(2.1rem,4vw,3.2rem)] font-bold leading-[0.95] tracking-[-0.04em]'>
                                Factura hoy, <span className='font-serif font-normal italic tracking-[-0.01em]'>decide</span> después
                            </h2>

                            <ul className='mt-6 flex flex-col gap-2'>
                                {trialPoints.map(point => (
                                    <li
                                        key={point}
                                        className='flex items-center gap-2.5 text-[15px] text-ink/80'
                                    >
                                        <span className='flex size-5 items-center justify-center rounded-full bg-ember/20'>
                                            <Check
                                                aria-hidden='true'
                                                className='size-3'
                                                strokeWidth={3}
                                            />
                                        </span>
                                        {point}
                                    </li>
                                ))}
                            </ul>

                            <div className='mt-8 flex flex-wrap items-center gap-3'>
                                <a
                                    href={START_HREF}
                                    className='group inline-flex h-12 items-center gap-3 rounded-full bg-ink pl-5 pr-1.5 text-[15px] font-medium text-cream transition-colors hover:bg-ink/85'
                                >
                                    {START_LABEL}
                                    <span className='flex size-9 items-center justify-center rounded-full bg-cream text-ink'>
                                        <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-45' />
                                    </span>
                                </a>
                                <a
                                    href={LOGIN_HREF}
                                    className='text-[15px] font-medium text-ink underline decoration-ink/25 underline-offset-4 transition-colors hover:decoration-ember'
                                >
                                    Ya tengo cuenta
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-3 lg:col-span-7'>
                        <FooterColumn
                            title='Producto'
                            links={footerProduct}
                        />

                        <FooterColumn
                            title='Recursos'
                            links={footerResources}
                        />

                        <FooterColumn
                            title='Empresa'
                            links={footerCompany}
                        />

                        <div className='col-span-2 flex flex-col gap-1.5 border-t border-ink/10 pt-6 text-[15px] sm:col-span-3'>
                            <p className={columnTitle}>Correo oficial de soporte</p>
                            <a
                                href={`mailto:${siteConfig.email}`}
                                className='mt-2 w-fit font-heading text-2xl font-bold tracking-[-0.03em] text-ink underline decoration-ember decoration-2 underline-offset-[6px]'
                            >
                                {siteConfig.email}
                            </a>
                            <p className='mt-2 max-w-xl text-sm leading-relaxed text-stone'>
                                {DGII_NOTICE}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div
                aria-hidden='true'
                className='mt-20 select-none px-3 pt-[3vw] md:mt-28 md:px-6'
            >
                <div className='flex items-end justify-between gap-[1vw] font-heading text-[min(21vw,24rem)] font-bold lowercase leading-[0.78] tracking-[-0.045em] text-ink'>
                    <LogoMark className='mb-[0.04em] size-[0.82em] lift-letter' />
                    {'himelio'.split('').map((letter, index) => (
                        <span
                            key={`${letter}-${index}`}
                            className='lift-letter'
                        >
                            {letter}
                        </span>
                    ))}
                </div>
            </div>

            <div className='mx-auto mt-8 flex max-w-[1400px] flex-col items-start justify-between gap-5 border-t border-ink/10 px-5 py-6 sm:flex-row sm:items-center md:px-10'>
                <p className='font-mono text-xs text-stone'>
                    © 2026 Himelio. Todos los derechos reservados.
                </p>

                <p className='font-mono text-xs text-stone'>
                    Facturación, POS e inventario en una sola pantalla.
                </p>

                <button
                    type='button'
                    onClick={toTop}
                    aria-label='Volver arriba'
                    data-cursor='Subir'
                    className='group stamp-round size-20 shrink-0 -rotate-12 flex-col gap-0.5 text-ember transition-transform duration-500 ease-expo hover:-translate-y-1 hover:rotate-0'
                >
                    <ArrowUp className='size-5 transition-transform duration-500 ease-expo group-hover:-translate-y-1' />
                    <span className='text-[8.5px] tracking-[0.18em]'>Arriba</span>
                </button>
            </div>
        </footer>
    )
}
